'use strict';

import { Vector3 } from 'three';
import { getWorldData } from '../../controls/orbitControls';
import { radToDeg } from 'three/src/math/MathUtils';
import gsap from 'gsap';
import { settingsData, threeJsVariables } from '../../../settings';
import { ViewMode } from '../../../common';

export class Disappear {
    static #objectsToDisappear = [];
    static #cameraVec = new Vector3();
    static #objectVec = new Vector3();
    static #targetVec = new Vector3();

    #mesh;
    #duration;
    #disappearDegrees;

    constructor (mesh,disappearDegrees = 45, duration = 0.3) {
        this.#mesh = mesh;
        this.#disappearDegrees = disappearDegrees;
        this.#duration = duration;
        this.hided = false;

        Disappear.#objectsToDisappear.push(this);
        this.update();
    }

    #applyEffect(state,material) {
        if (state === 'show') {
            gsap.to(material,{
                duration: this.#duration,
                opacity: material.userData.defaultSettings.opacity,
                onStart: () => {
                    this.#mesh.visible = true;
                    material.transparent = true;
                },
                onComplete: () => {
                    this.hided = false;
                    material.transparent = material.userData.defaultSettings.transparent
                }
            });
        } else {
            gsap.to(material,{
                duration: this.#duration,
                opacity: 0,
                onStart: () => {
                    material.transparent = true;
                },
                onComplete: () => this.#mesh.visible = false
            });
        }
    }

    hide() {
        if (this.hided) return;
        this.hided = true;

        if (this.#duration === 0) {
            this.#mesh.visible = false;
        } else {
            if (Array.isArray(this.#mesh.material)) {
                this.#mesh.material.forEach(material => this.#applyEffect('hide',material));
            } else {
                this.#applyEffect('hide',this.#mesh.material);
            }
        }
    }

    show() {
        if (this.hided) {

            if (this.#duration === 0) {
                this.hided = false;
                this.#mesh.visible = true;
            } else {
                if (Array.isArray(this.#mesh.material)) {
                    this.#mesh.material.forEach(material => this.#applyEffect('show',material));
                } else {
                    this.#applyEffect('show',this.#mesh.material);
                }
            }
        }
    }
    
    update() {
        Disappear.#targetVec.copy(threeJsVariables.orbitControls.target);
        Disappear.#targetVec.setY(0);
        Disappear.#objectVec.copy(this.#mesh.position);
        Disappear.#objectVec.setY(0);
        Disappear.#objectVec.sub(Disappear.#targetVec);
        Disappear.#cameraVec.copy(threeJsVariables.perspectiveCamera.position);
        Disappear.#cameraVec.setY(0);
        Disappear.#cameraVec.sub(Disappear.#targetVec);
    
        const angle = Disappear.#cameraVec.angleTo(Disappear.#objectVec);
        const degrees = radToDeg(angle);
    
        if (degrees < this.#disappearDegrees) {
            this.hide();
        } else {
            this.show();
        }
    }

    static update() {
        if (!Disappear.#objectsToDisappear.length || settingsData.activeViewMode !== ViewMode.Orbit) return;

        for (let i = 0; i < Disappear.#objectsToDisappear.length; i++) {
            Disappear.#objectsToDisappear[i].update();
        }
    }
}


export function prepareObjectsToDisappear () {
    const bundle = threeJsVariables.bundleConfig?.objectsDisappear || [];
    const fromConfig = config.features.objectsDisappear || [];
    const objects = bundle.concat(fromConfig);
    objects.forEach(data => {
       const object = threeJsVariables.scene.getObjectByName(data.name);
       if (object) {
           new Disappear(object,data.disappearDegrees,data.disappearDuration);
       }
    });
}
